import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { userIdAtom } from '@/services/globalState';
import { paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';
import { Sentry } from '@/util/sentry';

type SetContentLibraryItemLikeStatusV2Params =
  paths['/v2/content-library-items/{contentLibraryItemId}/like-status']['post']['parameters']['path'] &
    NonNullable<
      paths['/v2/content-library-items/{contentLibraryItemId}/like-status']['post']['requestBody']
    >['content']['application/json'];

export async function setContentLibraryItemLikeStatusV2({
  contentLibraryItemId,
  likeStatus,
}: SetContentLibraryItemLikeStatusV2Params): Promise<null> {
  await fetchClient.POST('/v2/content-library-items/{contentLibraryItemId}/like-status', {
    params: { path: { contentLibraryItemId } },
    body: { likeStatus },
  });

  return null;
}

export function useSetContentLibraryItemLikeStatusV2() {
  const queryClient = useQueryClient();

  const userId = useAtomValue(userIdAtom);

  return useMutation({
    mutationFn: setContentLibraryItemLikeStatusV2,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['contentLibraryItemLikeStatusV2', userId, variables.contentLibraryItemId],
      });

      queryClient.invalidateQueries({
        queryKey: ['contentLibraryItemsV2'],
      });
    },
    onError: (error, variables) => {
      Sentry.captureException(error, {
        tags: { context: 'contentLibrary:likeStatus' },
        extra: {
          contentLibraryItemId: variables.contentLibraryItemId,
          likeStatus: variables.likeStatus,
        },
      });
    },
  });
}
